import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRBAC } from './RBACContext';

interface OrgSwitcherProps {
  /** Tailwind classes applied to the trigger button wrapper. */
  className?: string;
}

/**
 * Minimal organisation switcher (placeholder).
 *
 * Shows the current org and, on click, expands a dropdown of organisations
 * the current user can switch into. Selecting one POSTs to
 * `/iam/api/organizations/switch/:orgId` (via `useRBAC().switchOrganization`)
 * and triggers a silent token refresh so the new `org_id` claim takes
 * effect on the next render.
 *
 * Surface area kept small on purpose — this is the baseline to unblock
 * super-admin org switching after the git clear. Polish (search box,
 * keyboard nav, recent-orgs pinning) can layer on later.
 */
export default function OrgSwitcher({ className }: OrgSwitcherProps) {
  const {
    organizations,
    currentOrgId,
    switchOrganization,
    isSuperAdmin,
    isPlatformSupport,
  } = useRBAC();

  const [open, setOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Anchor position for the portal-rendered dropdown panel. Derived
  // from the trigger button's bounding rect on open so the panel
  // floats outside the sidebar's `overflow-y` clip.
  const [panelPos, setPanelPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  // Close on outside click — the panel lives in a portal, so we also
  // ignore clicks inside it (otherwise selecting an org would
  // immediately dismiss the dropdown via the document handler).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        (containerRef.current && containerRef.current.contains(target)) ||
        (panelRef.current && panelRef.current.contains(target))
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Recompute the dropdown anchor whenever it opens (or the window
  // resizes / scrolls — those can shift the trigger).
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({
        // Align panel's bottom with trigger's bottom (matches old `bottom-0`).
        top: rect.bottom,
        // Sit to the right of the sidebar with the same 8-px gap.
        left: rect.right + 8,
      });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  if (organizations.length === 0) return null;

  // Regular users with a single org get nothing — there's nothing to switch.
  if (!isSuperAdmin && !isPlatformSupport && organizations.length < 2) {
    return null;
  }

  const currentOrg = organizations.find((o) => o.id === currentOrgId);
  const currentLabel = currentOrg?.name || currentOrgId || 'Select org';

  const handleSelect = async (orgId: string) => {
    if (orgId === currentOrgId) {
      setOpen(false);
      return;
    }
    setSwitchingId(orgId);
    try {
      await switchOrganization(orgId);
      toast.success('Switched organisation');
      // Silent renew inside switchOrganization will repaint the UI.
    } catch (err) {
      console.error('[OrgSwitcher] switch failed', err);
      toast.error(err instanceof Error ? err.message : 'Failed to switch organisation');
    } finally {
      setSwitchingId(null);
      setOpen(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex justify-center ${className ?? ''}`}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        // Styled to match SidebarButton so the org switcher slots into
        // the rest of the icon column instead of being a wider pill
        // that overflows / truncates inside the 64-px sidebar.
        className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200 ${
          open
            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
            : 'text-gray-500 hover:text-gray-900 hover:bg-white hover:shadow-sm'
        }`}
        title={`Active organisation: ${currentLabel}`}
      >
        <Building2 size={20} />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              top: panelPos.top,
              left: panelPos.left,
              // Anchor the panel's bottom-left to the computed point,
              // mirroring the old `bottom-0 left-full` behaviour.
              transform: 'translateY(-100%)',
            }}
            className="fixed z-[100] w-64 max-h-80 overflow-auto bg-white border border-gray-200 rounded-lg shadow-xl py-1"
            role="listbox"
          >
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
              Organisations
            </div>
            {organizations.map((org) => {
              const isActive = org.id === currentOrgId;
              const isSwitching = switchingId === org.id;
              return (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => handleSelect(org.id)}
                  disabled={isSwitching}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-xs text-left hover:bg-gray-50 transition-colors ${
                    isActive ? 'bg-blue-50/60' : ''
                  } disabled:opacity-60`}
                  role="option"
                  aria-selected={isActive}
                >
                  <Building2 size={14} className="text-gray-400 shrink-0" />
                  <span className="flex-1 truncate text-gray-800">{org.name}</span>
                  {isSwitching ? (
                    <Loader2 size={12} className="animate-spin text-blue-500 shrink-0" />
                  ) : isActive ? (
                    <Check size={12} className="text-blue-600 shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
